import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText } from 'lucide-react';

const FileUpload = ({ onFileUpload, loading, allowMultiple = false }) => {
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      if (allowMultiple) {
        onFileUpload(acceptedFiles);
      } else {
        onFileUpload(acceptedFiles[0]);
      }
    }
  }, [onFileUpload, allowMultiple]);

  const { getRootProps, getInputProps, isDragActive, acceptedFiles, fileRejections } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxFiles: allowMultiple ? undefined : 1,
    disabled: loading
  });

  return (
    <div>
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <input {...getInputProps()} />
        
        <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        
        {isDragActive ? (
          <p className="text-lg text-blue-800">
            Drop the SOF PDF{allowMultiple ? 's' : ''} here...
          </p>
        ) : (
          <div>
            <p className="text-lg text-gray-700 mb-2">
              Drag & drop SOF PDF{allowMultiple ? 's' : ''} here, or click to select
            </p>
            <p className="text-sm text-gray-500">
              Only PDF files are accepted{allowMultiple ? ' (multiple files supported)' : ''}
            </p>
          </div>
        )}
      </div>

      {acceptedFiles.length > 0 && (
        <div className="mt-4 space-y-2">
          {allowMultiple ? (
            <>
              <p className="text-sm font-medium text-gray-700">
                {acceptedFiles.length} file{acceptedFiles.length > 1 ? 's' : ''} selected:
              </p>
              {acceptedFiles.map((file, index) => (
                <div key={index} className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <FileText className="h-5 w-5 text-green-600 mr-2" />
                    <span className="text-sm text-green-800">{file.name}</span>
                  </div>
                </div>
              ))}
              {loading && (
                <div className="text-center">
                  <span className="text-sm text-green-600">Processing files...</span>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center">
                <FileText className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-sm text-green-800">
                  {acceptedFiles[0].name}
                </span>
              </div>
              {loading && (
                <span className="text-sm text-green-600">Processing...</span>
              )}
            </div>
          )}
        </div>
      )}

      {fileRejections.length > 0 && (
        <div className="mt-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">
              Please upload a valid PDF file
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;